// @ts-nocheck
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info, Lock, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import PlayerEditForm from '@/components/rosters/PlayerEditForm';
import { MissingInfoIcons, ClaimControls } from '@/components/rosters/PlayerStatus';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface Props {
  team: any;
}

function invokeError(error: any, data: any): string | null {
  if (error) return error.message ?? 'Request failed';
  if (data?.ok === false) return data.error?.message ?? 'Request failed';
  return null;
}

function dateRange(start?: string, end?: string) {
  if (!start) return '';
  if (!end || end === start) return start;
  return `${start} – ${end}`;
}

export default function TeamScheduledEventsView({ team }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  
  const [rows, setRows] = useState<any[]>([]);
  const [counts, setCounts] = useState<any>({ master_total: 0, participating: 0, guests: 0 });
  const [pendingIds, setPendingIds] = useState<Record<string, boolean>>({});
  const [editingId, setEditingId] = useState<string | null>(null);

  const canEdit = !!user;

  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['team-events', team.id],
    queryFn: async () => {
      const res = await invokeEdgeFunction('rle-list-team-events', {
        body: { team_id: team.id },
      });
      const err = invokeError(res.error, res.data);
      if (err) throw new Error(err);
      const payload = res.data?.data ?? res.data;
      return payload?.events ?? [];
    },
  });

  useEffect(() => {
    if (!selectedId && events.length > 0) {
      setSelectedId(events[0].event_registration_id);
    }
  }, [events, selectedId]);

  const selectedEvent = events.find((e: any) => e.event_registration_id === selectedId);

  const { data: participationData, isLoading: rosterLoading } = useQuery({
    queryKey: ['event-participation', selectedId],
    enabled: !!selectedId,
    queryFn: async () => {
      // `rle-project-master-roster` was deleted: projecting a master roster from
      // event rosters is a RecruitLook concept now, and it never functioned here.


      const listRes = await invokeEdgeFunction('rle-list-roster-participation', {
        body: { event_registration_id: selectedId },
      });
      const listErr = invokeError(listRes.error, listRes.data);
      if (listErr) throw new Error(listErr);

      return listRes.data?.data ?? listRes.data;
    },
  });

  const [guests, setGuests] = useState<any[]>([]);

  useEffect(() => {
    if (participationData) {
      const master = participationData.master ?? [];
      const guestList = participationData.guests ?? [];
      setRows(master);
      setGuests(guestList);
      setCounts(
        participationData.counts ?? {
          master_total: master.length,
          participating: master.filter((m: any) => m.participating).length,
          guests: guestList.length,
        }
      );
    }
  }, [participationData]);




  const toggleParticipation = async (player: any, nextState: boolean) => {
    if (pendingIds[player.player_id]) return;
    const prevRows = rows;
    const prevGuests = guests;
    const prevCounts = counts;

    const apply = (list: any[]) =>
      list.map((r) => (r.player_id === player.player_id ? { ...r, participating: nextState } : r));
    setRows(apply);
    setGuests(apply);
    setCounts((c) => ({ ...c, participating: c.participating + (nextState ? 1 : -1) }));
    setPendingIds((p) => ({ ...p, [player.player_id]: true }));

    try {
      const { data, error } = await invokeEdgeFunction('rle-set-roster-participation', {
        body: { event_registration_id: selectedId, player_id: player.player_id, participating: nextState },
      });
      const err = invokeError(error, data);
      if (err) throw new Error(err);
    } catch (err: any) {
      setRows(prevRows);
      setGuests(prevGuests);
      setCounts(prevCounts);
      toast.error(err.message ?? 'Failed to update participation');
    } finally {
      setPendingIds((p) => {
        const next = { ...p };
        delete next[player.player_id];
        return next;
      });
    }
  };

  if (eventsLoading) {
    return <Skeleton className="h-40 w-full" />;
  }

  if (events.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          This team isn't registered for any events yet.
        </CardContent>
      </Card>
    );
  }

  const masterTotal = counts.master_total ?? rows.length;

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['event-participation', selectedId] });

  const renderRow = (p: any, isGuestRow = false) => {
    if (editingId === p.player_id) {
      return (
        <li key={p.player_id} className="py-3">
          <PlayerEditForm
            player={p}
            teamId={team.id}
            eventRegistrationId={(isGuestRow || p.is_guest) ? selectedId : undefined}
            onCancel={() => setEditingId(null)}
            onSaved={() => { setEditingId(null); refresh(); }}
          />
        </li>
      );
    }
    const playing = !!p.participating;
    return (
      <li
        key={p.player_id}
        className={cn('flex items-center gap-3 py-2.5 px-2 bg-white even:bg-slate-50 hover:bg-slate-100', !playing && 'opacity-60')}
      >
        <Checkbox
          checked={playing}
          disabled={!canEdit || !!pendingIds[p.player_id] || selectedEvent?.roster_locked}
          onCheckedChange={(v) => toggleParticipation(p, !!v)}
          aria-label={`Toggle participation for ${p.name}`}
        />
        <Badge variant="outline" className="w-10 justify-center">
          {p.jersey_number ?? '—'}
        </Badge>
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="font-medium truncate">{p.name}</span>
          {(isGuestRow || p.is_guest) && <Badge variant="secondary">Guest</Badge>}
          <MissingInfoIcons missing={p.missing} />
        </div>
        <div className="ml-auto flex items-center gap-2">
          {playing ? (
            <Badge className="bg-green-600 hover:bg-green-600 text-white">Playing</Badge>
          ) : (
            <Badge variant="outline" className="text-muted-foreground">Not this event</Badge>
          )}
          <ClaimControls player={p} onChanged={refresh} />
          {canEdit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={() => setEditingId(p.player_id)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit</TooltipContent>
            </Tooltip>
          )}
        </div>
      </li>
    );
  };


  return (
    <div className="space-y-4">
      <Alert className="bg-cyan-50/60 dark:bg-cyan-950/20 border-cyan-200 dark:border-cyan-800 text-cyan-900 dark:text-cyan-100 [&>svg]:text-cyan-600 dark:[&>svg]:text-cyan-400">
        <Info className="h-4 w-4" />
        <AlertDescription>
          Pick an event below. Your full roster is shown — everyone starts as playing. Uncheck anyone
          not attending this event. To add or remove players permanently, use the Team roster tab.
        </AlertDescription>
      </Alert>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {events.map((e: any) => {
          const active = e.event_registration_id === selectedId;
          return (
            <button
              key={e.event_registration_id}
              onClick={() => setSelectedId(e.event_registration_id)}
              className={cn(
                'text-left rounded-lg border p-4 transition-colors',
                active ? 'border-primary ring-1 ring-primary bg-primary/5' : 'hover:border-primary/50'
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium truncate">{e.event_name}</p>
                {e.roster_locked && <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {dateRange(e.event_start_date, e.event_end_date)}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {e.playing_count ?? 0} of {e.roster_size ?? 0} playing
              </p>
            </button>
          );
        })}
      </div>

      {selectedEvent && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle>Playing at {selectedEvent.event_name}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {counts.participating} of {masterTotal} playing
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {rosterLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : rows.length === 0 && guests.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">
                No players on this team yet. Add players under the Team roster tab.
              </p>
            ) : (
              <TooltipProvider>
                {rows.length > 0 && (
                  <ul className="divide-y">
                    {rows.map((p: any) => renderRow(p))}
                  </ul>
                )}

                {guests.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      Guests
                      <span className="ml-1 font-normal normal-case">
                        — {guests.filter((g: any) => g.participating).length} of {guests.length} on this event
                      </span>
                    </p>
                    <ul className="divide-y">
                      {guests.map((p: any) => renderRow(p, true))}
                    </ul>
                  </div>
                )}
              </TooltipProvider>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
